import { ParentProps, createSignal, createContext, useContext, onMount, onCleanup, Accessor, Setter } from 'solid-js'
import styles from './st-drop-target.module.css'

export type DropItem = {
    id: string;          // ID from Draggable component
    data: any;           // Item data from Draggable
};

type DropTargetContextType = {
    isHovered: Accessor<boolean>;
};

const DropTargetContext = createContext<DropTargetContextType>();

export const useDropTarget = () => {
    const context = useContext(DropTargetContext);
    if (!context) {
        throw new Error('useDropTarget must be used within a DropTarget component');
    }
    return context;
};

export class DropTargetProps {
    disabled?: boolean = false;
    accepts?: string[] = [];
    hoverClass?: string;
    onDrop?: (item: DropItem) => void;
}

// Global registry accessible to st-draggable
export type DropTargetRegistration = {
    id: string;
    element: HTMLElement;
    accepts: string[];
    disabled: boolean;
    setIsHovered: Setter<boolean>;
    onDrop?: (item: DropItem) => void;
};

const dropTargetRegistry = new Map<string, DropTargetRegistration>();
let dropTargetIdCounter = 0;

// Export for use by st-draggable
export function getDropTargetRegistry() {
    return dropTargetRegistry;
}

export default function DropTarget(props: ParentProps<DropTargetProps>) {
    const [isHovered, setIsHovered] = createSignal(false);
    const dropTargetId = `droptarget-${++dropTargetIdCounter}`;
    let containerRef!: HTMLDivElement;

    onMount(() => {
        dropTargetRegistry.set(dropTargetId, {
            id: dropTargetId,
            element: containerRef,
            accepts: props.accepts || [],
            disabled: props.disabled || false,
            setIsHovered,
            onDrop: props.onDrop
        });
    });

    onCleanup(() => {
        dropTargetRegistry.delete(dropTargetId);
    });

    return (
        <DropTargetContext.Provider value={{ isHovered }}>
            <div
                ref={containerRef}
                class={isHovered() && props.hoverClass ? props.hoverClass : styles['st-drop-target']}
                data-droptarget-id={dropTargetId}
            >
                {props.children}
            </div>
        </DropTargetContext.Provider>
    );
}

DropTarget.defaultProps = new DropTargetProps();
